import { REGEX } from '@/constant';
import { useEditableDetail } from '@/hooks';
import { cn, dayjs, formatDate } from '@/utils';
import { ClockCircleOutlined } from '@ant-design/icons';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';

import { DatePickerProps, TimePicker as TimePickerAntd } from 'antd';
import locale from 'antd/es/date-picker/locale/ko_KR';

import { Dayjs } from 'dayjs';
import { isNilOrEmpty } from 'ramda-adjunct';
import type { BaseInfo, PickerRef } from 'rc-picker/lib/interface';

export interface IDatePickerProps extends DatePickerProps {
	label?: string;
	inputClassName?: string;
	containerClassName?: string;
	labelClassName?: string;
	horizontal?: boolean;
	touched?: boolean;
	errorCode?: string | undefined;
	hasServerError?: boolean;
	format?: string;
	require?: boolean;
	isDetail?: boolean;
}

const TimePicker: React.FC<IDatePickerProps> = ({
	label,
	placeholder = '',
	inputClassName,
	containerClassName,
	horizontal = true,
	labelClassName,
	required,
	touched,
	errorCode,
	hasServerError,
	format,
	require,
	isDetail = false,
	...rest
}) => {
	const { isUpdate, editable, handleLabelClick } = useEditableDetail({
		fieldName: rest.name as string,
	});

	const DATE_FORMAT = format || ['HH:mm', 'HHmm'];

	const [open, setOpen] = useState(false);

	let value: Dayjs | null | undefined = useMemo(() => {
		if (isNilOrEmpty(rest.value)) {
			return null;
		}

		if (typeof rest.value === 'string') {
			return dayjs(rest.value, DATE_FORMAT);
		}
		return rest.value || null;
	}, [rest.value]);

	const timePickerRef = useRef<PickerRef>(null);

	useEffect(() => {
		const inputElement = timePickerRef.current?.nativeElement;

		const handleInput = (event: Event) => {
			const target = event.target as HTMLInputElement;
			const value = target.value;
			const cleanValue = value.replace(/[-,]/g, '');
			if (cleanValue !== value) {
				target.value = cleanValue;
			}
		};

		if (inputElement) {
			inputElement.addEventListener('keydown', handleKeyDown);
			inputElement.addEventListener('input', handleInput);
		}
		return () => {
			if (inputElement) {
				inputElement.removeEventListener('keydown', handleKeyDown);
				inputElement.removeEventListener('input', handleInput);
			}
		};
	}, [timePickerRef?.current]);

	const handleKeyDown = (event: any) => {
		const key = event.key;
		let allowedControlKeys = REGEX.DATE_TIME_INPUT_ALLOWED_KEYS_REGEX;

		if (!allowedControlKeys.test(key)) {
			event.preventDefault();
		}

		if (key === 'Enter') {
			timePickerRef?.current?.blur();
		}
	};

	const handleFocus = (e: React.FocusEvent<HTMLElement>, info: BaseInfo) => {
		const input = e.target as HTMLInputElement;
		input.setAttribute('inputmode', 'numeric');
		input.setAttribute('pattern', '[0-9]*');
	};

	useEffect(() => {
		if (editable && isUpdate && isDetail) {
			timePickerRef?.current?.focus();
			setOpen(true);
		}
	}, [editable]);

	return (
		<div
			className={cn(
				'flex ',
				horizontal ? 'items-center' : 'flex-col',
				containerClassName
			)}
		>
			{label && (
				<label
					className={cn(
						'text-[13px] font-medium text-font2 min-w-[110px]',
						labelClassName
					)}
				>
					{label} {require && <span className="text-red6">*</span>}
				</label>
			)}

			{isDetail && !editable ? (
				<span
					className={cn(
						'box-border w-full text-[13px] h-[30px] text-font2 py-[5px] px-[11px] text-left overflow-hidden hover:border hover:border-gray1 cursor-text',
						rest.disabled &&
							'cursor-not-allowed hover:border-none hover:bg-porcelain'
					)}
					onClick={!rest.disabled ? handleLabelClick : undefined}
				>
					{formatDate(value, { outputFormat: format || 'HH:mm' }) || '-'}
				</span>
			) : (
				<TimePickerAntd
					{...rest}
					value={value}
					size="large"
					format={DATE_FORMAT}
					placeholder={placeholder}
					suffixIcon={<ClockCircleOutlined />}
					className={cn(
						'xl:rounded-none h-[30px] text-[13px]',
						inputClassName,
						((touched && errorCode) || hasServerError) &&
							'border-red8 border-[1.5px]'
					)}
					locale={{
						...locale,
						lang: {
							...locale.lang,
							now: 'Now',
							ok: 'Ok',
						},
					}}
					ref={timePickerRef}
					open={open}
					onOpenChange={setOpen}
					onBlur={() => {
						window.scrollTo(0, 0);
					}}
					onFocus={handleFocus}
				/>
			)}
		</div>
	);
};

export default memo(TimePicker);
